import {
  DEFAULT_ALWAYS_ZERO_LIMIT,
  DEFAULT_ZERO_STOCK_THRESHOLD,
  type OrganizationSettings as SharedOrganizationSettings,
} from '@threadheads/shared';

// BLU-495 Q4: AvailabilitySettings / DEFAULT_AVAILABILITY_SETTINGS / resolveAvailabilitySettings
// were removed. The Availability Back-Sync card in StockSyncSettings.tsx that
// consumed them was deprecated in favor of `safetyBufferPercent` on the
// inventoryPush card. See .planning/phases/55-blu-495-q4-deprecate-back-sync/.

export type PurchaseOrderSettingsData = {
  numberPrefix: string;
  numberFormat: string;
  numberPadding: number;
  draftPrefix: string;
  senderEmail: string;
  replyToEmail: string;
  internalCcEmail: string;
  /** BLU-1268: the Xero account just-in-time PO bills go to; '' = unset (the push refuses). */
  justInTimeAccountCode: string;
};

export const DEFAULT_PO_SETTINGS: PurchaseOrderSettingsData = {
  numberPrefix: 'PO',
  numberFormat: '{PREFIX}-{CC}-{NUM}',
  numberPadding: 3,
  draftPrefix: 'DRAFT',
  senderEmail: '',
  replyToEmail: '',
  internalCcEmail: '',
  justInTimeAccountCode: '',
};

export type InventoryPushHaltedInfo = {
  reason: string;
  skuCount: number;
  detectedAt: string;
  skuSamples: Array<{ sku: string; previousQty: number; newQty: number }>;
};

export type InventoryPushSettings = {
  enabled: boolean;
  autoSyncEnabled: boolean;
  dryRunMode: boolean;
  seedInventory: boolean;
  intervalMinutes: number;
  safetyBuffer: number;
  /** BLU-495 Q4: % of each SKU's notify_threshold to hold back from Shopify. null = off. */
  safetyBufferPercent: number | null;
  zeroStockThreshold: number;
  /** BLU-1541: most Always 0 SKUs one scheduled push may take to 0 before it pauses. 0 = No limit. */
  alwaysZeroLimit: number;
  approvalEnabled: boolean;
  dropUnitsThreshold: number;
  dropPercentThreshold: number;
  zeroOutProtection: boolean;
  /** UTC hour (0-23) to run nightly POD variant pre-discovery. null = disabled. */
  discoveryHourUtc: number | null;
  /** New POD variants discovered per push cycle. null = use the env default (MAX_NEW_DISCOVERIES_PER_CYCLE). */
  discoveriesPerCycle: number | null;
  /** Auto-enable inventory tracking on discovered POD variants and push stock immediately. */
  autoEnableTracking: boolean;
  /** Auto-stock (activate) products at Shopify locations when pushing if not already stocked there. */
  autoStockAtLocation: boolean;
  /** Include Product Sync-approved Shopify drafts in future inventory fan-out. */
  pushApprovedDraftProducts: boolean;
  /** Periodically discover Product Sync-approved products when they become active. */
  autoDiscoverNewActiveProducts: boolean;
  /** Hidden rollout boundary: unmatched variants require Product Sync approval. */
  requireProductSyncInventoryApproval: boolean;
  /** Set inventoryPolicy=DENY on discovered POD variants still "continue selling", so qty 0 is unsellable. */
  enforceDenyPolicy: boolean;
  /** BLU-677: push to Shopify immediately when a purchase order is received (accelerates the scheduled push). */
  pushOnPurchaseOrderReceive: boolean;
  /** BLU-677: push to Shopify immediately when a stock transfer is received. */
  pushOnTransferReceive: boolean;
  /** BLU-677: push to Shopify immediately on a manual stock adjustment. */
  pushOnStockAdjustment: boolean;
  /** BLU-677: push to Shopify immediately when a stocktake is committed (can be a large bulk push). */
  pushOnStocktake: boolean;
  /** BLU-343: Location UUIDs excluded from auto-push. (Per-SKU Skip / Always 0
   *  rules moved to `organizations.push_sku_rules` in BLU-1541 and are not part
   *  of this settings document, so no form save can send or clobber them.) */
  excludedLocationIds: string[];
  // BLU-354c: legacy `hybridQty` + `hybridLocationIds` removed. Hybrid
  // (PAP + blank) push is controlled by the Pre-Print Hybrid Mode toggle
  // in production settings — single source of truth. UI reads the set via
  // `trpc.productionSettings.getOrgHybridLocationIds`.
  halted: InventoryPushHaltedInfo | null;
};

export const DEFAULT_INVENTORY_PUSH_SETTINGS: InventoryPushSettings = {
  enabled: false,
  autoSyncEnabled: false,
  dryRunMode: true,
  seedInventory: true,
  intervalMinutes: 5,
  safetyBuffer: 0,
  safetyBufferPercent: null,
  // The push worker's own defaults, so an org with nothing saved reads the
  // threshold and the Always 0 limit the worker actually uses.
  zeroStockThreshold: DEFAULT_ZERO_STOCK_THRESHOLD,
  alwaysZeroLimit: DEFAULT_ALWAYS_ZERO_LIMIT,
  approvalEnabled: false,
  dropUnitsThreshold: 50,
  dropPercentThreshold: 80,
  zeroOutProtection: true,
  discoveryHourUtc: null,
  discoveriesPerCycle: null,
  autoEnableTracking: false,
  autoStockAtLocation: true,
  // Existing orgs carry explicit migration-backfilled values; these are the
  // safer defaults for a newly-created settings document.
  pushApprovedDraftProducts: false,
  autoDiscoverNewActiveProducts: true,
  requireProductSyncInventoryApproval: true,
  enforceDenyPolicy: false,
  pushOnPurchaseOrderReceive: false,
  pushOnTransferReceive: false,
  pushOnStockAdjustment: false,
  pushOnStocktake: false,
  excludedLocationIds: [],
  halted: null,
};

export function resolveInventoryPushSettings(
  settings: SharedOrganizationSettings | null | undefined,
): InventoryPushSettings {
  const push = settings?.inventoryPush;
  // Cast-gated for mixed-version deployments where the frontend may load
  // before the freshly-built shared package declaration reaches the runtime.
  const eligibility = push as (Record<string, unknown> & {
    pushApprovedDraftProducts?: boolean;
    autoDiscoverNewActiveProducts?: boolean;
    requireProductSyncInventoryApproval?: boolean;
  }) | undefined;
  return {
    enabled: push?.enabled ?? DEFAULT_INVENTORY_PUSH_SETTINGS.enabled,
    autoSyncEnabled: push?.autoSyncEnabled ?? DEFAULT_INVENTORY_PUSH_SETTINGS.autoSyncEnabled,
    dryRunMode: push?.dryRunMode ?? DEFAULT_INVENTORY_PUSH_SETTINGS.dryRunMode,
    seedInventory: push?.seedInventory ?? DEFAULT_INVENTORY_PUSH_SETTINGS.seedInventory,
    intervalMinutes: push?.intervalMinutes ?? DEFAULT_INVENTORY_PUSH_SETTINGS.intervalMinutes,
    safetyBuffer: push?.safetyBuffer ?? DEFAULT_INVENTORY_PUSH_SETTINGS.safetyBuffer,
    safetyBufferPercent: push?.safetyBufferPercent ?? DEFAULT_INVENTORY_PUSH_SETTINGS.safetyBufferPercent,
    zeroStockThreshold: push?.zeroStockThreshold ?? DEFAULT_INVENTORY_PUSH_SETTINGS.zeroStockThreshold,
    alwaysZeroLimit: push?.alwaysZeroLimit ?? DEFAULT_INVENTORY_PUSH_SETTINGS.alwaysZeroLimit,
    approvalEnabled: push?.approvalEnabled ?? DEFAULT_INVENTORY_PUSH_SETTINGS.approvalEnabled,
    dropUnitsThreshold: push?.dropUnitsThreshold ?? DEFAULT_INVENTORY_PUSH_SETTINGS.dropUnitsThreshold,
    dropPercentThreshold: push?.dropPercentThreshold ?? DEFAULT_INVENTORY_PUSH_SETTINGS.dropPercentThreshold,
    zeroOutProtection: push?.zeroOutProtection ?? DEFAULT_INVENTORY_PUSH_SETTINGS.zeroOutProtection,
    discoveryHourUtc: push?.discoveryHourUtc ?? DEFAULT_INVENTORY_PUSH_SETTINGS.discoveryHourUtc,
    discoveriesPerCycle: push?.discoveriesPerCycle ?? DEFAULT_INVENTORY_PUSH_SETTINGS.discoveriesPerCycle,
    autoEnableTracking: push?.autoEnableTracking ?? DEFAULT_INVENTORY_PUSH_SETTINGS.autoEnableTracking,
    autoStockAtLocation: push?.autoStockAtLocation ?? DEFAULT_INVENTORY_PUSH_SETTINGS.autoStockAtLocation,
    pushApprovedDraftProducts: eligibility?.pushApprovedDraftProducts ?? DEFAULT_INVENTORY_PUSH_SETTINGS.pushApprovedDraftProducts,
    autoDiscoverNewActiveProducts: eligibility?.autoDiscoverNewActiveProducts ?? DEFAULT_INVENTORY_PUSH_SETTINGS.autoDiscoverNewActiveProducts,
    requireProductSyncInventoryApproval: eligibility?.requireProductSyncInventoryApproval ?? DEFAULT_INVENTORY_PUSH_SETTINGS.requireProductSyncInventoryApproval,
    enforceDenyPolicy: push?.enforceDenyPolicy ?? DEFAULT_INVENTORY_PUSH_SETTINGS.enforceDenyPolicy,
    pushOnPurchaseOrderReceive: (push as Record<string, unknown>)?.pushOnPurchaseOrderReceive as boolean ?? DEFAULT_INVENTORY_PUSH_SETTINGS.pushOnPurchaseOrderReceive,
    pushOnTransferReceive: (push as Record<string, unknown>)?.pushOnTransferReceive as boolean ?? DEFAULT_INVENTORY_PUSH_SETTINGS.pushOnTransferReceive,
    pushOnStockAdjustment: (push as Record<string, unknown>)?.pushOnStockAdjustment as boolean ?? DEFAULT_INVENTORY_PUSH_SETTINGS.pushOnStockAdjustment,
    pushOnStocktake: (push as Record<string, unknown>)?.pushOnStocktake as boolean ?? DEFAULT_INVENTORY_PUSH_SETTINGS.pushOnStocktake,
    excludedLocationIds: (push as Record<string, unknown>)?.excludedLocationIds as string[] ?? DEFAULT_INVENTORY_PUSH_SETTINGS.excludedLocationIds,
    halted: push?.halted ?? null,
  };
}

export type NotificationSettings = {
  slackWebhookUrl: string | null;
  slackEnabled: boolean;
  notifyOnHalt: boolean;
  notifyOnPendingApproval: boolean;
  notifyOnPushError: boolean;
  notifyOnPushSummary: boolean;
  /** PO behind-pace daily digest (per-phase target times). */
  notifyOnPoBehindPace: boolean;
  /** PO status-update digest (transitions in the last 24h). */
  notifyOnPoStatusUpdates: boolean;
  /** Weekly starred-item stock digest (replaces the daily OOS roll-up). */
  notifyOnOosDigest: boolean;
  /** Slack channel ID/name the OOS digest posts to (via Threadbot). */
  oosDigestChannel: string | null;
  /** Which SKUs the OOS digest reports. */
  oosDigestMode: 'key_items' | 'all_items' | 'threshold';
  /** Active organization members @mentioned in the digest channel. */
  oosDigestRecipientIds: string[];
  /** Exclude pre-printed stock (POD-printed-ahead held as PAP) from the OOS digest.
   *  BLU-409: opt-out — defaults to true; an org unchecks this to see them again. */
  oosDigestExcludePreprinted: boolean;
  /** Weekly pipeline digest (Bulk/Blanks PO counts + Product Dev in progress). */
  notifyOnPipelineDigest: boolean;
  /** Slack channel ID/name the pipeline digest posts to (via Threadbot). */
  pipelineDigestChannel: string | null;
  /** Weekly hustle digest — the nag: overdue Product Dev styles, stale styles
   *  (no movement in `hustleDigestStaleDays`), and overdue POs. */
  notifyOnHustleDigest: boolean;
  /** Slack channel ID/name the hustle digest posts to (via Threadbot). */
  hustleDigestChannel: string | null;
  /** A style with no update in this many days counts as stale (1-90, default 14). */
  hustleDigestStaleDays: number;
  /** Daily push digest (replaces the per-cycle Push Summary webhook). */
  notifyOnPushDigest: boolean;
  /** Slack channel ID/name the push digest posts to (via Threadbot). */
  pushDigestChannel: string | null;
  /** Daily production digest (previous AEST day's orders/prints/packs/redos). */
  notifyOnProductionDaily: boolean;
  /** Weekly Monday production roll-up with week-over-week deltas. */
  notifyOnProductionWeekly: boolean;
  /** Slack channel ID/name both production digests post to (via Threadbot). */
  productionDigestChannel: string | null;
  /** Weekly Monday incoming-shipments digest (in-transit POs by destination). */
  notifyOnIncomingShipments: boolean;
  /** Slack channel ID/name the incoming-shipments digest posts to (via Threadbot). */
  incomingShipmentsChannel: string | null;
  /** Partner portal (supplier-org side): notify when a buyer submits a PO to us. */
  notifyOnPartnerPoSubmit: boolean;
  /** Slack channel ID/name the partner PO-submitted notify posts to (via Threadbot). */
  partnerPoSubmitChannel: string | null;
  /** Product Dev collaboration: per-event Slack posts when a partner-worked record changes. */
  notifyOnProductDevUpdates: boolean;
  /** Slack channel ID/name the Product Dev updates post to (via Threadbot). */
  productDevUpdatesChannel: string | null;
  /** Include sample events (requested/received/approved) in Product Dev updates. */
  notifyOnProductDevSamples: boolean;
  /** Include stage/status changes in Product Dev updates. */
  notifyOnProductDevStages: boolean;
  /** Include new comments in Product Dev updates. */
  notifyOnProductDevComments: boolean;
  /** Include file uploads in Product Dev updates. */
  notifyOnProductDevFiles: boolean;
};

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  slackWebhookUrl: null,
  slackEnabled: false,
  notifyOnHalt: true,
  notifyOnPendingApproval: true,
  notifyOnPushError: true,
  notifyOnPushSummary: false,
  notifyOnPoBehindPace: true,
  notifyOnPoStatusUpdates: true,
  notifyOnOosDigest: false,
  oosDigestChannel: null,
  oosDigestMode: 'key_items',
  oosDigestRecipientIds: [],
  oosDigestExcludePreprinted: true,
  notifyOnPipelineDigest: false,
  pipelineDigestChannel: null,
  notifyOnHustleDigest: false,
  hustleDigestChannel: null,
  hustleDigestStaleDays: 14,
  notifyOnPushDigest: false,
  pushDigestChannel: null,
  notifyOnProductionDaily: false,
  notifyOnProductionWeekly: false,
  productionDigestChannel: null,
  notifyOnIncomingShipments: false,
  incomingShipmentsChannel: null,
  notifyOnPartnerPoSubmit: false,
  partnerPoSubmitChannel: null,
  notifyOnProductDevUpdates: false,
  productDevUpdatesChannel: null,
  notifyOnProductDevSamples: true,
  notifyOnProductDevStages: true,
  notifyOnProductDevComments: false,
  notifyOnProductDevFiles: false,
};

export function resolveNotificationSettings(
  settings: SharedOrganizationSettings | null | undefined,
): NotificationSettings {
  const n = settings?.inventoryPush?.notifications as
    | (Record<string, unknown> & {
        slackWebhookUrl?: string | null;
        slackEnabled?: boolean;
        notifyOnHalt?: boolean;
        notifyOnPendingApproval?: boolean;
        notifyOnPushError?: boolean;
        notifyOnPushSummary?: boolean;
        notifyOnPoBehindPace?: boolean;
        notifyOnPoStatusUpdates?: boolean;
        notifyOnOosDigest?: boolean;
        oosDigestChannel?: string | null;
        oosDigestMode?: 'key_items' | 'all_items' | 'threshold';
        oosDigestRecipientIds?: string[];
        oosDigestExcludePreprinted?: boolean;
        notifyOnPipelineDigest?: boolean;
        pipelineDigestChannel?: string | null;
        notifyOnHustleDigest?: boolean;
        hustleDigestChannel?: string | null;
        hustleDigestStaleDays?: number;
        notifyOnPushDigest?: boolean;
        pushDigestChannel?: string | null;
        notifyOnProductionDaily?: boolean;
        notifyOnProductionWeekly?: boolean;
        productionDigestChannel?: string | null;
        notifyOnIncomingShipments?: boolean;
        incomingShipmentsChannel?: string | null;
        notifyOnPartnerPoSubmit?: boolean;
        partnerPoSubmitChannel?: string | null;
        notifyOnProductDevUpdates?: boolean;
        productDevUpdatesChannel?: string | null;
        notifyOnProductDevSamples?: boolean;
        notifyOnProductDevStages?: boolean;
        notifyOnProductDevComments?: boolean;
        notifyOnProductDevFiles?: boolean;
      })
    | undefined;
  return {
    slackWebhookUrl: n?.slackWebhookUrl ?? DEFAULT_NOTIFICATION_SETTINGS.slackWebhookUrl,
    slackEnabled: n?.slackEnabled ?? DEFAULT_NOTIFICATION_SETTINGS.slackEnabled,
    notifyOnHalt: n?.notifyOnHalt ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnHalt,
    notifyOnPendingApproval: n?.notifyOnPendingApproval ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPendingApproval,
    notifyOnPushError: n?.notifyOnPushError ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPushError,
    notifyOnPushSummary: n?.notifyOnPushSummary ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPushSummary,
    notifyOnPoBehindPace: n?.notifyOnPoBehindPace ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPoBehindPace,
    notifyOnPoStatusUpdates: n?.notifyOnPoStatusUpdates ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPoStatusUpdates,
    notifyOnOosDigest: n?.notifyOnOosDigest ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnOosDigest,
    oosDigestChannel: n?.oosDigestChannel ?? DEFAULT_NOTIFICATION_SETTINGS.oosDigestChannel,
    oosDigestMode: n?.oosDigestMode ?? DEFAULT_NOTIFICATION_SETTINGS.oosDigestMode,
    oosDigestRecipientIds: Array.isArray(n?.oosDigestRecipientIds)
      ? n.oosDigestRecipientIds.filter((id): id is string => typeof id === 'string')
      : DEFAULT_NOTIFICATION_SETTINGS.oosDigestRecipientIds,
    oosDigestExcludePreprinted: n?.oosDigestExcludePreprinted ?? DEFAULT_NOTIFICATION_SETTINGS.oosDigestExcludePreprinted,
    notifyOnPipelineDigest: n?.notifyOnPipelineDigest ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPipelineDigest,
    pipelineDigestChannel: n?.pipelineDigestChannel ?? DEFAULT_NOTIFICATION_SETTINGS.pipelineDigestChannel,
    notifyOnHustleDigest: n?.notifyOnHustleDigest ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnHustleDigest,
    hustleDigestChannel: n?.hustleDigestChannel ?? DEFAULT_NOTIFICATION_SETTINGS.hustleDigestChannel,
    hustleDigestStaleDays: n?.hustleDigestStaleDays ?? DEFAULT_NOTIFICATION_SETTINGS.hustleDigestStaleDays,
    notifyOnPushDigest: n?.notifyOnPushDigest ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPushDigest,
    pushDigestChannel: n?.pushDigestChannel ?? DEFAULT_NOTIFICATION_SETTINGS.pushDigestChannel,
    notifyOnProductionDaily: n?.notifyOnProductionDaily ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductionDaily,
    notifyOnProductionWeekly: n?.notifyOnProductionWeekly ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductionWeekly,
    productionDigestChannel: n?.productionDigestChannel ?? DEFAULT_NOTIFICATION_SETTINGS.productionDigestChannel,
    notifyOnIncomingShipments: n?.notifyOnIncomingShipments ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnIncomingShipments,
    incomingShipmentsChannel: n?.incomingShipmentsChannel ?? DEFAULT_NOTIFICATION_SETTINGS.incomingShipmentsChannel,
    notifyOnPartnerPoSubmit: n?.notifyOnPartnerPoSubmit ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnPartnerPoSubmit,
    partnerPoSubmitChannel: n?.partnerPoSubmitChannel ?? DEFAULT_NOTIFICATION_SETTINGS.partnerPoSubmitChannel,
    notifyOnProductDevUpdates: n?.notifyOnProductDevUpdates ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductDevUpdates,
    productDevUpdatesChannel: n?.productDevUpdatesChannel ?? DEFAULT_NOTIFICATION_SETTINGS.productDevUpdatesChannel,
    notifyOnProductDevSamples: n?.notifyOnProductDevSamples ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductDevSamples,
    notifyOnProductDevStages: n?.notifyOnProductDevStages ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductDevStages,
    notifyOnProductDevComments: n?.notifyOnProductDevComments ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductDevComments,
    notifyOnProductDevFiles: n?.notifyOnProductDevFiles ?? DEFAULT_NOTIFICATION_SETTINGS.notifyOnProductDevFiles,
  };
}

export function resolvePurchaseOrderSettings(
  settings: SharedOrganizationSettings | null | undefined,
): PurchaseOrderSettingsData {
  const purchaseOrders = settings?.purchaseOrders;
  return {
    numberPrefix: purchaseOrders?.numberPrefix ?? DEFAULT_PO_SETTINGS.numberPrefix,
    numberFormat: purchaseOrders?.numberFormat ?? DEFAULT_PO_SETTINGS.numberFormat,
    numberPadding: purchaseOrders?.numberPadding ?? DEFAULT_PO_SETTINGS.numberPadding,
    draftPrefix: purchaseOrders?.draftPrefix ?? DEFAULT_PO_SETTINGS.draftPrefix,
    senderEmail: purchaseOrders?.senderEmail ?? DEFAULT_PO_SETTINGS.senderEmail,
    replyToEmail: purchaseOrders?.replyToEmail ?? DEFAULT_PO_SETTINGS.replyToEmail,
    internalCcEmail: purchaseOrders?.internalCcEmail ?? DEFAULT_PO_SETTINGS.internalCcEmail,
    justInTimeAccountCode: purchaseOrders?.justInTimeAccountCode ?? DEFAULT_PO_SETTINGS.justInTimeAccountCode,
  };
}
